<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '../stores/auth'
import { api } from '../api'

const authStore = useAuthStore()

interface CompletionTag {
  id: string
  name: string
  slug: string
  kind: string
  color: string
  icon: string | null
  description: string | null
  sort_order: number
}

interface CompletionSubitem {
  id: string
  item_id: string
  name: string
  complete: boolean
  notes: string | null
  sort_order: number
}

interface CompletionSupply {
  id: string
  item_id: string
  name: string
  quantity: number | null
  unit: string | null
  acquired: boolean
  est_cost: number | null
  source_url: string | null
  notes: string | null
  sort_order: number
}

interface CompletionItem {
  id: string
  category_id: string
  name: string
  complete: boolean
  notes: string | null
  sort_order: number
  priority: number
  est_hours: number | null
  tags: string[]
  subitems: CompletionSubitem[]
  supplies: CompletionSupply[]
}

interface CompletionCategory {
  id: string
  name: string
  icon: string
  sort_order: number
  items: CompletionItem[]
}

interface ItemGroup {
  key: string
  label: string
  icon: string
  hint: string
  items: CompletionItem[]
}

const categories = ref<CompletionCategory[]>([])
const tags = ref<CompletionTag[]>([])
const loading = ref(true)
const error = ref('')

// View state
type GroupMode = 'category' | 'tag' | 'priority'
const groupBy = ref<GroupMode>('category')
const activeTagIds = ref<Set<string>>(new Set())
const hideComplete = ref(false)
const expandedItems = ref<Set<string>>(new Set())
const showSupplyList = ref(false)

// Edit state
const editingItem = ref<CompletionItem | null>(null)
const editingCategory = ref<CompletionCategory | null>(null)
const showAddCategory = ref(false)
const showAddItem = ref<string | null>(null) // category id
const editingTagsFor = ref<string | null>(null) // item id
const showAddSubitem = ref<string | null>(null)
const showAddSupply = ref<string | null>(null)

// Form data
const newCategory = ref({ name: '', icon: '' })
const newItem = ref({ name: '', notes: '' })
const newSubitem = ref({ name: '' })
const newSupply = ref({ name: '', quantity: '', unit: '', est_cost: '' })

const PRIORITIES = [
  { value: 1, label: 'Do first', chip: 'bg-red-100 text-red-700 border-red-200' },
  { value: 2, label: 'Soon', chip: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 3, label: 'Normal', chip: 'bg-gray-100 text-gray-600 border-gray-200' },
  { value: 4, label: 'Later', chip: 'bg-sky-100 text-sky-700 border-sky-200' },
  { value: 5, label: 'Someday', chip: 'bg-slate-100 text-slate-500 border-slate-200' },
]

// Tailwind purges classes it cannot see, so tag colours are a static map
// rather than an interpolated class name.
const TAG_COLORS: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  green: 'bg-green-100 text-green-800 border-green-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  red: 'bg-red-100 text-red-800 border-red-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200',
  teal: 'bg-teal-100 text-teal-800 border-teal-200',
  slate: 'bg-slate-100 text-slate-800 border-slate-200',
  gray: 'bg-gray-100 text-gray-700 border-gray-200',
}

function tagChipClass(tag: CompletionTag | undefined, active = false): string {
  const base = TAG_COLORS[tag?.color || 'gray'] || TAG_COLORS.gray
  return active ? `${base} ring-2 ring-offset-1 ring-current` : base
}

const tagById = computed(() => {
  const map = new Map<string, CompletionTag>()
  for (const tag of tags.value) map.set(tag.id, tag)
  return map
})

function priorityMeta(priority: number) {
  return PRIORITIES.find(p => p.value === priority) || PRIORITIES[2]
}

async function fetchData() {
  loading.value = true
  try {
    const data = await api.get('/api/completion')
    categories.value = (data.categories || []).map((cat: any) => ({
      ...cat,
      items: (cat.items || []).map((item: any) => ({
        ...item,
        priority: item.priority ?? 3,
        tags: item.tags || [],
        subitems: item.subitems || [],
        supplies: item.supplies || [],
      })),
    }))
    tags.value = data.tags || []
    error.value = ''
  } catch (e) {
    error.value = 'Failed to load completion data'
    console.error(e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchData)

// ---------------------------------------------------------------- grouping

const allItems = computed(() => categories.value.flatMap(cat => cat.items))

function categoryOf(item: CompletionItem): CompletionCategory | undefined {
  return categories.value.find(cat => cat.id === item.category_id)
}

const visibleItems = computed(() => {
  let items = allItems.value
  if (hideComplete.value) items = items.filter(i => !i.complete)
  if (activeTagIds.value.size > 0) {
    // AND semantics: "before winter AND needs the lift" is the useful query
    items = items.filter(i => Array.from(activeTagIds.value).every(t => i.tags.includes(t)))
  }
  return items
})

const groups = computed<ItemGroup[]>(() => {
  const items = visibleItems.value

  if (groupBy.value === 'tag') {
    const out: ItemGroup[] = []
    for (const tag of tags.value) {
      const tagged = items.filter(i => i.tags.includes(tag.id))
      if (tagged.length === 0) continue
      out.push({
        key: tag.id,
        label: tag.name,
        icon: tag.icon || '🏷️',
        hint: tag.description || '',
        items: tagged,
      })
    }
    const untagged = items.filter(i => i.tags.length === 0)
    if (untagged.length > 0) {
      out.push({ key: 'untagged', label: 'Untagged', icon: '⬜', hint: 'No tags yet', items: untagged })
    }
    return out
  }

  if (groupBy.value === 'priority') {
    return PRIORITIES.map(p => ({
      key: `priority-${p.value}`,
      label: p.label,
      icon: ['🔥', '⏫', '➖', '🕒', '💤'][p.value - 1],
      hint: '',
      items: items.filter(i => (i.priority ?? 3) === p.value),
    })).filter(g => g.items.length > 0)
  }

  return categories.value
    .map(cat => ({
      key: cat.id,
      label: cat.name,
      icon: cat.icon,
      hint: '',
      items: items.filter(i => i.category_id === cat.id),
    }))
    .filter(g => g.items.length > 0 || (!hideComplete.value && activeTagIds.value.size === 0))
})

function toggleTagFilter(tagId: string) {
  if (activeTagIds.value.has(tagId)) activeTagIds.value.delete(tagId)
  else activeTagIds.value.add(tagId)
  activeTagIds.value = new Set(activeTagIds.value)
}

function tagCount(tagId: string): number {
  return allItems.value.filter(i => i.tags.includes(tagId) && !i.complete).length
}

// ------------------------------------------------------------- statistics

function groupPercent(group: ItemGroup): number {
  if (group.items.length === 0) return 0
  return Math.round((group.items.filter(i => i.complete).length / group.items.length) * 100)
}

const overallPercent = computed(() => {
  const total = allItems.value.length
  if (total === 0) return 0
  return Math.round((allItems.value.filter(i => i.complete).length / total) * 100)
})

const totalItems = computed(() => allItems.value.length)
const completeItems = computed(() => allItems.value.filter(i => i.complete).length)

const winterTag = computed(() => tags.value.find(t => t.slug === 'before-winter'))
const liftTag = computed(() => tags.value.find(t => t.slug === 'scissor-lift'))

const winterOutstanding = computed(() =>
  winterTag.value ? allItems.value.filter(i => i.tags.includes(winterTag.value!.id) && !i.complete).length : 0)
const liftOutstanding = computed(() =>
  liftTag.value ? allItems.value.filter(i => i.tags.includes(liftTag.value!.id) && !i.complete).length : 0)

// The shopping list: everything still needed for work that is not done.
const outstandingSupplies = computed(() => {
  const rows: Array<{ supply: CompletionSupply; item: CompletionItem; category?: CompletionCategory }> = []
  for (const item of allItems.value) {
    if (item.complete) continue
    for (const supply of item.supplies) {
      if (supply.acquired) continue
      rows.push({ supply, item, category: categoryOf(item) })
    }
  }
  return rows
})

const supplyGroups = computed(() => {
  const map = new Map<string, { label: string; icon: string; rows: typeof outstandingSupplies.value }>()
  for (const row of outstandingSupplies.value) {
    const key = row.category?.id || 'other'
    if (!map.has(key)) {
      map.set(key, { label: row.category?.name || 'Other', icon: row.category?.icon || '📦', rows: [] })
    }
    map.get(key)!.rows.push(row)
  }
  return Array.from(map.values())
})

const supplyEstimate = computed(() =>
  outstandingSupplies.value.reduce((sum, r) => sum + (r.supply.est_cost || 0), 0))

// ---------------------------------------------------------------- actions

function toggleItemExpanded(id: string) {
  if (expandedItems.value.has(id)) expandedItems.value.delete(id)
  else expandedItems.value.add(id)
  expandedItems.value = new Set(expandedItems.value)
}

async function toggleItemComplete(item: CompletionItem) {
  if (!authStore.isOwner) return
  try {
    await api.patch(`/api/completion/items/${item.id}`, { complete: !item.complete })
    item.complete = !item.complete
  } catch (e) {
    console.error('Failed to toggle item:', e)
  }
}

async function setPriority(item: CompletionItem, priority: number) {
  if (!authStore.isOwner) return
  const previous = item.priority
  item.priority = priority
  try {
    await api.patch(`/api/completion/items/${item.id}`, { priority })
  } catch (e) {
    item.priority = previous
    console.error('Failed to set priority:', e)
  }
}

async function toggleItemTag(item: CompletionItem, tagId: string) {
  if (!authStore.isOwner) return
  const next = item.tags.includes(tagId)
    ? item.tags.filter(t => t !== tagId)
    : [...item.tags, tagId]
  const previous = item.tags
  item.tags = next
  try {
    await api.put(`/api/completion/items/${item.id}/tags`, { tag_ids: next })
  } catch (e) {
    item.tags = previous
    console.error('Failed to update tags:', e)
  }
}

async function addSubitem(item: CompletionItem) {
  if (!newSubitem.value.name.trim()) return
  try {
    await api.post(`/api/completion/items/${item.id}/subitems`, {
      name: newSubitem.value.name,
      sort_order: item.subitems.length,
    })
    newSubitem.value = { name: '' }
    showAddSubitem.value = null
    await fetchData()
  } catch (e) {
    console.error('Failed to add sub-item:', e)
  }
}

async function toggleSubitem(sub: CompletionSubitem) {
  if (!authStore.isOwner) return
  try {
    await api.patch(`/api/completion/subitems/${sub.id}`, { complete: !sub.complete })
    sub.complete = !sub.complete
  } catch (e) {
    console.error('Failed to toggle sub-item:', e)
  }
}

async function deleteSubitem(sub: CompletionSubitem) {
  if (!confirm(`Delete "${sub.name}"?`)) return
  try {
    await api.delete(`/api/completion/subitems/${sub.id}`)
    await fetchData()
  } catch (e) {
    console.error('Failed to delete sub-item:', e)
  }
}

async function addSupply(item: CompletionItem) {
  if (!newSupply.value.name.trim()) return
  try {
    await api.post(`/api/completion/items/${item.id}/supplies`, {
      name: newSupply.value.name,
      quantity: newSupply.value.quantity ? Number(newSupply.value.quantity) : null,
      unit: newSupply.value.unit || null,
      est_cost: newSupply.value.est_cost ? Number(newSupply.value.est_cost) : null,
      sort_order: item.supplies.length,
    })
    newSupply.value = { name: '', quantity: '', unit: '', est_cost: '' }
    showAddSupply.value = null
    await fetchData()
  } catch (e) {
    console.error('Failed to add supply:', e)
  }
}

async function toggleSupplyAcquired(supply: CompletionSupply) {
  if (!authStore.isOwner) return
  try {
    await api.patch(`/api/completion/supplies/${supply.id}`, { acquired: !supply.acquired })
    supply.acquired = !supply.acquired
  } catch (e) {
    console.error('Failed to update supply:', e)
  }
}

async function deleteSupply(supply: CompletionSupply) {
  if (!confirm(`Remove "${supply.name}"?`)) return
  try {
    await api.delete(`/api/completion/supplies/${supply.id}`)
    await fetchData()
  } catch (e) {
    console.error('Failed to delete supply:', e)
  }
}

async function saveItem() {
  if (!editingItem.value) return
  try {
    await api.patch(`/api/completion/items/${editingItem.value.id}`, {
      name: editingItem.value.name,
      notes: editingItem.value.notes,
      priority: editingItem.value.priority,
      est_hours: editingItem.value.est_hours,
    })
    editingItem.value = null
    await fetchData()
  } catch (e) {
    console.error('Failed to save item:', e)
  }
}

async function deleteItem(item: CompletionItem) {
  if (!confirm(`Delete "${item.name}"?`)) return
  try {
    await api.delete(`/api/completion/items/${item.id}`)
    await fetchData()
  } catch (e) {
    console.error('Failed to delete item:', e)
  }
}

async function addItem(categoryId: string) {
  if (!newItem.value.name.trim()) return
  try {
    await api.post('/api/completion/items', {
      category_id: categoryId,
      name: newItem.value.name,
      notes: newItem.value.notes || null,
      complete: false,
    })
    newItem.value = { name: '', notes: '' }
    showAddItem.value = null
    await fetchData()
  } catch (e) {
    console.error('Failed to add item:', e)
  }
}

async function saveCategory() {
  if (!editingCategory.value) return
  try {
    await api.patch(`/api/completion/categories/${editingCategory.value.id}`, {
      name: editingCategory.value.name,
      icon: editingCategory.value.icon,
    })
    editingCategory.value = null
    await fetchData()
  } catch (e) {
    console.error('Failed to save category:', e)
  }
}

async function deleteCategory(category: CompletionCategory) {
  if (!confirm(`Delete "${category.name}" and all its items?`)) return
  try {
    await api.delete(`/api/completion/categories/${category.id}`)
    await fetchData()
  } catch (e) {
    console.error('Failed to delete category:', e)
  }
}

async function addCategory() {
  if (!newCategory.value.name.trim() || !newCategory.value.icon.trim()) return
  try {
    await api.post('/api/completion/categories', {
      name: newCategory.value.name,
      icon: newCategory.value.icon,
      sort_order: categories.value.length,
    })
    newCategory.value = { name: '', icon: '' }
    showAddCategory.value = false
    await fetchData()
  } catch (e) {
    console.error('Failed to add category:', e)
  }
}

// Problem report form
const showReportForm = ref(false)
const reportForm = ref({
  category: '',
  location: '',
  description: '',
  severity: 'low',
})
const reportSubmitting = ref(false)
const reportSuccess = ref(false)

async function submitReport() {
  reportSubmitting.value = true
  await new Promise(resolve => setTimeout(resolve, 1000))
  console.log('Problem report:', {
    ...reportForm.value,
    reporter: authStore.user?.name,
    reportedAt: new Date().toISOString(),
  })
  reportSubmitting.value = false
  reportSuccess.value = true
  setTimeout(() => {
    showReportForm.value = false
    reportSuccess.value = false
    reportForm.value = { category: '', location: '', description: '', severity: 'low' }
  }, 2000)
}

const expandedCategories = ref<Set<string>>(new Set())

function toggleCategory(key: string) {
  if (expandedCategories.value.has(key)) expandedCategories.value.delete(key)
  else expandedCategories.value.add(key)
  expandedCategories.value = new Set(expandedCategories.value)
}
</script>

<template>
  <div class="max-w-4xl mx-auto p-4 md:p-6">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">House Completion</h1>
        <p class="text-gray-600">Construction progress tracker</p>
      </div>
      <div class="flex gap-2">
        <button
          v-if="authStore.isOwner"
          @click="showAddCategory = true"
          class="btn bg-gray-100 hover:bg-gray-200 text-gray-700"
        >
          + Category
        </button>
        <button
          @click="showReportForm = true"
          class="btn btn-primary"
        >
          Report Problem
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-12">
      <p class="text-gray-600">Loading...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <p class="text-red-700">{{ error }}</p>
    </div>

    <template v-else>
      <!-- Overall Progress -->
      <div class="card mb-4 bg-gradient-to-r from-primary-50 to-blue-50">
        <div class="text-center">
          <div class="text-5xl font-bold text-primary-700 mb-2">{{ overallPercent }}%</div>
          <div class="text-gray-600">Overall Completion</div>
          <div class="text-sm text-gray-500 mt-1">{{ completeItems }} of {{ totalItems }} items complete</div>
          <div class="w-full bg-gray-200 rounded-full h-4 mt-4">
            <div
              class="bg-primary-600 h-4 rounded-full transition-all duration-500"
              :style="{ width: `${overallPercent}%` }"
            ></div>
          </div>
        </div>
      </div>

      <!-- At-a-glance: the two questions that drive scheduling -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <button
          v-if="winterTag"
          @click="groupBy = 'tag'; activeTagIds = new Set([winterTag.id])"
          class="card text-left hover:ring-2 hover:ring-blue-300"
        >
          <div class="text-2xl">❄️</div>
          <div class="text-xl font-bold text-blue-700">{{ winterOutstanding }}</div>
          <div class="text-xs text-gray-600">left before winter</div>
        </button>
        <button
          v-if="liftTag"
          @click="groupBy = 'tag'; activeTagIds = new Set([liftTag.id])"
          class="card text-left hover:ring-2 hover:ring-amber-300"
        >
          <div class="text-2xl">🛗</div>
          <div class="text-xl font-bold text-amber-700">{{ liftOutstanding }}</div>
          <div class="text-xs text-gray-600">need the scissor lift — batch these</div>
        </button>
        <button
          @click="showSupplyList = true"
          class="card text-left hover:ring-2 hover:ring-teal-300"
        >
          <div class="text-2xl">🧾</div>
          <div class="text-xl font-bold text-teal-700">{{ outstandingSupplies.length }}</div>
          <div class="text-xs text-gray-600">supplies to buy{{ supplyEstimate ? ` · ~$${Math.round(supplyEstimate)}` : '' }}</div>
        </button>
      </div>

      <!-- Toolbar: grouping + tag filters -->
      <div class="card mb-4">
        <div class="flex flex-wrap items-center gap-3">
          <div class="flex items-center gap-1">
            <span class="text-sm text-gray-500 mr-1">Group by</span>
            <button
              v-for="mode in (['category', 'tag', 'priority'] as const)"
              :key="mode"
              @click="groupBy = mode"
              class="px-3 py-1 text-sm rounded-full border capitalize"
              :class="groupBy === mode
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'"
            >
              {{ mode }}
            </button>
          </div>
          <label class="flex items-center gap-2 text-sm text-gray-600 ml-auto">
            <input type="checkbox" v-model="hideComplete" />
            Hide completed
          </label>
        </div>

        <div v-if="tags.length" class="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
          <button
            v-for="tag in tags"
            :key="tag.id"
            @click="toggleTagFilter(tag.id)"
            class="px-2 py-1 text-xs rounded-full border transition"
            :class="tagChipClass(tag, activeTagIds.has(tag.id))"
            :title="tag.description || ''"
          >
            {{ tag.icon }} {{ tag.name }}
            <span class="opacity-60">{{ tagCount(tag.id) }}</span>
          </button>
          <button
            v-if="activeTagIds.size"
            @click="activeTagIds = new Set()"
            class="px-2 py-1 text-xs text-gray-500 underline"
          >
            clear
          </button>
        </div>
        <p v-if="activeTagIds.size > 1" class="text-xs text-gray-500 mt-2">
          Showing items that carry <strong>all</strong> selected tags.
        </p>
      </div>

      <!-- Groups -->
      <div class="space-y-4">
        <div v-for="group in groups" :key="group.key" class="card">
          <div class="flex items-center justify-between">
            <button
              @click="toggleCategory(group.key)"
              class="flex-1 flex items-center gap-3 text-left"
            >
              <span class="text-2xl">{{ group.icon }}</span>
              <div>
                <h2 class="font-bold text-gray-800">{{ group.label }}</h2>
                <p class="text-sm text-gray-500">
                  {{ group.items.filter(i => i.complete).length }}/{{ group.items.length }} complete
                  <span v-if="group.hint" class="text-gray-400">· {{ group.hint }}</span>
                </p>
              </div>
            </button>
            <div class="flex items-center gap-4">
              <span class="text-xl font-bold" :class="{
                'text-green-600': groupPercent(group) === 100,
                'text-yellow-600': groupPercent(group) >= 50 && groupPercent(group) < 100,
                'text-orange-600': groupPercent(group) < 50,
              }">
                {{ groupPercent(group) }}%
              </span>
              <!-- Admin: edit/delete only makes sense for real categories -->
              <div v-if="authStore.isOwner && groupBy === 'category'" class="flex gap-1">
                <button @click.stop="editingCategory = { ...categories.find(c => c.id === group.key)! }" class="p-1 text-gray-400 hover:text-blue-600">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                </button>
                <button @click.stop="deleteCategory(categories.find(c => c.id === group.key)!)" class="p-1 text-gray-400 hover:text-red-600">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
              <button @click="toggleCategory(group.key)" class="text-2xl text-gray-400">
                {{ expandedCategories.has(group.key) ? '−' : '+' }}
              </button>
            </div>
          </div>

          <!-- Progress bar -->
          <div class="w-full bg-gray-200 rounded-full h-2 mt-3">
            <div
              class="h-2 rounded-full transition-all duration-500"
              :class="{
                'bg-green-500': groupPercent(group) === 100,
                'bg-yellow-500': groupPercent(group) >= 50 && groupPercent(group) < 100,
                'bg-orange-500': groupPercent(group) < 50,
              }"
              :style="{ width: `${groupPercent(group)}%` }"
            ></div>
          </div>

          <!-- Expanded item list -->
          <div v-if="expandedCategories.has(group.key)" class="mt-4 space-y-2">
            <div
              v-for="item in group.items"
              :key="item.id"
              class="rounded-lg"
              :class="item.complete ? 'bg-green-50' : 'bg-gray-50'"
            >
              <div class="flex items-start justify-between py-2 px-3">
                <div class="flex items-start gap-3 flex-1 min-w-0">
                  <button
                    v-if="authStore.isOwner"
                    @click="toggleItemComplete(item)"
                    :class="item.complete ? 'text-green-600' : 'text-gray-400 hover:text-green-600'"
                    class="text-lg leading-6"
                  >
                    {{ item.complete ? '✓' : '○' }}
                  </button>
                  <span v-else :class="item.complete ? 'text-green-600' : 'text-gray-400'" class="text-lg leading-6">
                    {{ item.complete ? '✓' : '○' }}
                  </span>
                  <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <span :class="item.complete ? 'text-green-800' : 'text-gray-700'">{{ item.name }}</span>
                      <span
                        v-for="tagId in item.tags"
                        :key="tagId"
                        class="text-[10px] px-1.5 py-0.5 rounded-full border"
                        :class="tagChipClass(tagById.get(tagId))"
                      >
                        {{ tagById.get(tagId)?.icon }} {{ tagById.get(tagId)?.name }}
                      </span>
                    </div>
                    <p v-if="item.notes && !item.complete" class="text-xs text-orange-600">{{ item.notes }}</p>
                    <div class="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                      <button @click="toggleItemExpanded(item.id)" class="text-primary-600 hover:text-primary-800">
                        {{ expandedItems.has(item.id) ? 'Hide details' : 'Details' }}
                      </button>
                      <span v-if="item.subitems.length">
                        ☑ {{ item.subitems.filter(s => s.complete).length }}/{{ item.subitems.length }} steps
                      </span>
                      <span v-if="item.supplies.length">
                        📦 {{ item.supplies.filter(s => !s.acquired).length }}/{{ item.supplies.length }} supplies to get
                      </span>
                      <span v-if="item.est_hours">⏱ {{ item.est_hours }}h</span>
                    </div>
                  </div>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <!-- Priority: click cycles through when you own the place -->
                  <select
                    v-if="authStore.isOwner"
                    :value="item.priority"
                    @change="setPriority(item, Number(($event.target as HTMLSelectElement).value))"
                    class="text-xs rounded-full border px-2 py-1"
                    :class="priorityMeta(item.priority).chip"
                  >
                    <option v-for="p in PRIORITIES" :key="p.value" :value="p.value">{{ p.label }}</option>
                  </select>
                  <span
                    v-else
                    class="text-xs px-2 py-1 rounded-full border"
                    :class="priorityMeta(item.priority).chip"
                  >
                    {{ priorityMeta(item.priority).label }}
                  </span>
                  <div v-if="authStore.isOwner" class="flex gap-1">
                    <button @click="editingItem = { ...item }" class="p-1 text-gray-400 hover:text-blue-600">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button @click="deleteItem(item)" class="p-1 text-gray-400 hover:text-red-600">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Item details: tags, steps, supplies -->
              <div v-if="expandedItems.has(item.id)" class="px-3 pb-3 pl-10 space-y-3 border-t border-gray-200/70 pt-3">
                <!-- Tag editor -->
                <div v-if="authStore.isOwner">
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Tags</span>
                    <button
                      @click="editingTagsFor = editingTagsFor === item.id ? null : item.id"
                      class="text-xs text-primary-600 hover:text-primary-800"
                    >
                      {{ editingTagsFor === item.id ? 'done' : 'edit' }}
                    </button>
                  </div>
                  <div v-if="editingTagsFor === item.id" class="flex flex-wrap gap-2 mt-2">
                    <button
                      v-for="tag in tags"
                      :key="tag.id"
                      @click="toggleItemTag(item, tag.id)"
                      class="px-2 py-1 text-xs rounded-full border"
                      :class="item.tags.includes(tag.id) ? tagChipClass(tag, true) : 'bg-white text-gray-500 border-gray-300'"
                    >
                      {{ tag.icon }} {{ tag.name }}
                    </button>
                  </div>
                </div>

                <!-- Sub-items -->
                <div>
                  <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Steps</span>
                  <div v-if="item.subitems.length" class="mt-1 space-y-1">
                    <div v-for="sub in item.subitems" :key="sub.id" class="flex items-center gap-2 text-sm">
                      <button
                        v-if="authStore.isOwner"
                        @click="toggleSubitem(sub)"
                        :class="sub.complete ? 'text-green-600' : 'text-gray-400 hover:text-green-600'"
                      >
                        {{ sub.complete ? '☑' : '☐' }}
                      </button>
                      <span v-else>{{ sub.complete ? '☑' : '☐' }}</span>
                      <span :class="sub.complete ? 'line-through text-gray-400' : 'text-gray-700'">{{ sub.name }}</span>
                      <button
                        v-if="authStore.isOwner"
                        @click="deleteSubitem(sub)"
                        class="ml-auto text-xs text-gray-300 hover:text-red-500"
                      >✕</button>
                    </div>
                  </div>
                  <p v-else class="text-xs text-gray-400 mt-1">No steps yet.</p>
                  <div v-if="authStore.isOwner" class="mt-2">
                    <div v-if="showAddSubitem === item.id" class="flex gap-2">
                      <input
                        v-model="newSubitem.name"
                        type="text"
                        placeholder="Step"
                        class="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        @keyup.enter="addSubitem(item)"
                      />
                      <button @click="addSubitem(item)" class="px-3 py-1 bg-primary-600 text-white rounded text-sm">Add</button>
                      <button @click="showAddSubitem = null" class="px-3 py-1 bg-gray-200 rounded text-sm">Cancel</button>
                    </div>
                    <button v-else @click="showAddSubitem = item.id" class="text-xs text-primary-600 hover:text-primary-800">
                      + Add step
                    </button>
                  </div>
                </div>

                <!-- Supplies -->
                <div>
                  <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Supplies</span>
                  <div v-if="item.supplies.length" class="mt-1 space-y-1">
                    <div v-for="supply in item.supplies" :key="supply.id" class="flex items-center gap-2 text-sm">
                      <button
                        v-if="authStore.isOwner"
                        @click="toggleSupplyAcquired(supply)"
                        :class="supply.acquired ? 'text-green-600' : 'text-gray-400 hover:text-green-600'"
                        :title="supply.acquired ? 'On hand' : 'Still to buy'"
                      >
                        {{ supply.acquired ? '☑' : '🛒' }}
                      </button>
                      <span v-else>{{ supply.acquired ? '☑' : '🛒' }}</span>
                      <span :class="supply.acquired ? 'text-gray-400' : 'text-gray-700'">
                        <template v-if="supply.quantity">{{ supply.quantity }}{{ supply.unit ? ' ' + supply.unit : '' }} — </template>{{ supply.name }}
                      </span>
                      <span v-if="supply.est_cost" class="text-xs text-gray-400">~${{ supply.est_cost }}</span>
                      <a v-if="supply.source_url" :href="supply.source_url" target="_blank" rel="noopener" class="text-xs text-primary-600 underline">link</a>
                      <button
                        v-if="authStore.isOwner"
                        @click="deleteSupply(supply)"
                        class="ml-auto text-xs text-gray-300 hover:text-red-500"
                      >✕</button>
                    </div>
                  </div>
                  <p v-else class="text-xs text-gray-400 mt-1">Nothing needed.</p>
                  <div v-if="authStore.isOwner" class="mt-2">
                    <div v-if="showAddSupply === item.id" class="flex flex-wrap gap-2">
                      <input v-model="newSupply.name" type="text" placeholder="Supply" class="flex-1 min-w-[8rem] border border-gray-300 rounded px-2 py-1 text-sm" @keyup.enter="addSupply(item)" />
                      <input v-model="newSupply.quantity" type="number" step="any" placeholder="Qty" class="w-16 border border-gray-300 rounded px-2 py-1 text-sm" />
                      <input v-model="newSupply.unit" type="text" placeholder="Unit" class="w-20 border border-gray-300 rounded px-2 py-1 text-sm" />
                      <input v-model="newSupply.est_cost" type="number" step="any" placeholder="$" class="w-20 border border-gray-300 rounded px-2 py-1 text-sm" />
                      <button @click="addSupply(item)" class="px-3 py-1 bg-primary-600 text-white rounded text-sm">Add</button>
                      <button @click="showAddSupply = null" class="px-3 py-1 bg-gray-200 rounded text-sm">Cancel</button>
                    </div>
                    <button v-else @click="showAddSupply = item.id" class="text-xs text-primary-600 hover:text-primary-800">
                      + Add supply
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add item button (admin only, category grouping only) -->
            <div v-if="authStore.isOwner && groupBy === 'category'" class="pt-2">
              <div v-if="showAddItem === group.key" class="flex gap-2">
                <input
                  v-model="newItem.name"
                  type="text"
                  placeholder="Item name"
                  class="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                  @keyup.enter="addItem(group.key)"
                />
                <input
                  v-model="newItem.notes"
                  type="text"
                  placeholder="Notes (optional)"
                  class="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <button @click="addItem(group.key)" class="px-3 py-1 bg-primary-600 text-white rounded text-sm">Add</button>
                <button @click="showAddItem = null; newItem = { name: '', notes: '' }" class="px-3 py-1 bg-gray-200 rounded text-sm">Cancel</button>
              </div>
              <button
                v-else
                @click="showAddItem = group.key"
                class="text-sm text-primary-600 hover:text-primary-800"
              >
                + Add item
              </button>
            </div>
          </div>
        </div>

        <p v-if="groups.length === 0" class="text-center text-gray-500 py-8">
          Nothing matches those filters.
        </p>
      </div>

      <!-- Social Login Roadmap -->
      <div class="card mt-8">
        <h2 class="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span>🔐</span> Authentication Roadmap
        </h2>
        <p class="text-gray-600 text-sm mb-4">Future social login options to implement:</p>

        <div class="space-y-3">
          <div class="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
            <div class="flex items-center gap-3">
              <svg class="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <div>
                <p class="font-medium text-green-800">Google OAuth</p>
                <p class="text-xs text-green-600">Currently implemented</p>
              </div>
            </div>
            <span class="text-green-600 font-bold">Live</span>
          </div>

          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div class="flex items-center gap-3">
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="#000">
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
              </svg>
              <div>
                <p class="font-medium text-gray-800">GitHub</p>
                <p class="text-xs text-gray-500">Easy - Similar OAuth flow</p>
              </div>
            </div>
            <span class="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Easy</span>
          </div>

          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div class="flex items-center gap-3">
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="#5865F2">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              <div>
                <p class="font-medium text-gray-800">Discord</p>
                <p class="text-xs text-gray-500">Easy - Great for tech users</p>
              </div>
            </div>
            <span class="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Easy</span>
          </div>

          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div class="flex items-center gap-3">
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="#000">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <div>
                <p class="font-medium text-gray-800">X (Twitter)</p>
                <p class="text-xs text-gray-500">Medium - OAuth 2.0 with PKCE</p>
              </div>
            </div>
            <span class="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">Medium</span>
          </div>

          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div class="flex items-center gap-3">
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="#000">
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
              </svg>
              <div>
                <p class="font-medium text-gray-800">Apple</p>
                <p class="text-xs text-gray-500">Hard - $99/yr developer account</p>
              </div>
            </div>
            <span class="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">Hard</span>
          </div>
        </div>

        <div class="mt-4 p-3 bg-blue-50 rounded-lg">
          <p class="text-sm text-blue-700">
            <strong>Recommendation:</strong> Add GitHub and Discord next.
          </p>
        </div>
      </div>
    </template>

    <!-- Edit Item Modal -->
    <div v-if="editingItem" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="card max-w-md w-full">
        <h2 class="text-lg font-bold text-gray-800 mb-4">Edit Item</h2>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input v-model="editingItem.name" type="text" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input v-model="editingItem.notes" type="text" class="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Optional notes" />
          </div>
          <div class="flex gap-3">
            <button @click="editingItem = null" class="flex-1 px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button @click="saveItem" class="flex-1 btn btn-primary">Save</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Category Modal -->
    <div v-if="editingCategory" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="card max-w-md w-full">
        <h2 class="text-lg font-bold text-gray-800 mb-4">Edit Category</h2>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input v-model="editingCategory.name" type="text" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Icon (emoji)</label>
            <input v-model="editingCategory.icon" type="text" class="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div class="flex gap-3">
            <button @click="editingCategory = null" class="flex-1 px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button @click="saveCategory" class="flex-1 btn btn-primary">Save</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Category Modal -->
    <div v-if="showAddCategory" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="card max-w-md w-full">
        <h2 class="text-lg font-bold text-gray-800 mb-4">Add Category</h2>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input v-model="newCategory.name" type="text" class="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., Bathroom" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Icon (emoji)</label>
            <input v-model="newCategory.icon" type="text" class="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., 🛁" />
          </div>
          <div class="flex gap-3">
            <button @click="showAddCategory = false; newCategory = { name: '', icon: '' }" class="flex-1 px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button @click="addCategory" class="flex-1 btn btn-primary">Add</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Report Problem Modal -->
    <div v-if="showReportForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold text-gray-800">Report a Problem</h2>
          <button @click="showReportForm = false" class="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <div v-if="reportSuccess" class="text-center py-8">
          <div class="text-4xl mb-4">✅</div>
          <p class="text-green-600 font-medium">Report submitted successfully!</p>
        </div>

        <form v-else @submit.prevent="submitReport" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select v-model="reportForm.category" required class="w-full border border-gray-300 rounded-lg px-3 py-2">
              <option value="">Select category...</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.name">
                {{ cat.icon }} {{ cat.name }}
              </option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Location in House</label>
            <input
              v-model="reportForm.location"
              type="text"
              required
              placeholder="e.g., Master bedroom, Kitchen"
              class="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <div class="flex gap-4">
              <label class="flex items-center gap-2">
                <input type="radio" v-model="reportForm.severity" value="low" />
                <span class="text-sm">Low</span>
              </label>
              <label class="flex items-center gap-2">
                <input type="radio" v-model="reportForm.severity" value="medium" />
                <span class="text-sm">Medium</span>
              </label>
              <label class="flex items-center gap-2">
                <input type="radio" v-model="reportForm.severity" value="high" />
                <span class="text-sm">High</span>
              </label>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="reportForm.description"
              required
              rows="4"
              placeholder="Describe the problem..."
              class="w-full border border-gray-300 rounded-lg px-3 py-2"
            ></textarea>
          </div>

          <div class="flex gap-3 pt-2">
            <button type="button" @click="showReportForm = false" class="flex-1 px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" :disabled="reportSubmitting" class="flex-1 btn btn-primary">
              {{ reportSubmitting ? 'Submitting...' : 'Submit' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    <!-- Supplies shopping list (rolled up across every unfinished item) -->
    <div v-if="showSupplyList" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg font-bold text-gray-800">🧾 Supplies to get</h2>
            <p class="text-sm text-gray-500">
              {{ outstandingSupplies.length }} item{{ outstandingSupplies.length === 1 ? '' : 's' }}
              <span v-if="supplyEstimate"> · about ${{ Math.round(supplyEstimate) }}</span>
            </p>
          </div>
          <button @click="showSupplyList = false" class="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <p v-if="outstandingSupplies.length === 0" class="text-gray-500 text-center py-8">
          Nothing outstanding — every supply is marked as on hand.
        </p>

        <div v-for="group in supplyGroups" :key="group.label" class="mb-4">
          <h3 class="font-medium text-gray-700 mb-2">{{ group.icon }} {{ group.label }}</h3>
          <div class="space-y-1">
            <div
              v-for="row in group.rows"
              :key="row.supply.id"
              class="flex items-start gap-2 text-sm bg-gray-50 rounded px-2 py-1"
            >
              <button
                v-if="authStore.isOwner"
                @click="toggleSupplyAcquired(row.supply)"
                class="text-gray-400 hover:text-green-600"
                title="Mark as bought / on hand"
              >🛒</button>
              <span v-else>🛒</span>
              <div class="flex-1 min-w-0">
                <span class="text-gray-800">
                  <template v-if="row.supply.quantity">{{ row.supply.quantity }}{{ row.supply.unit ? ' ' + row.supply.unit : '' }} — </template>{{ row.supply.name }}
                </span>
                <div class="text-xs text-gray-500 truncate">for {{ row.item.name }}</div>
              </div>
              <span v-if="row.supply.est_cost" class="text-xs text-gray-400 shrink-0">~${{ row.supply.est_cost }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
